import { useMutation, useQuery } from '@apollo/client'
import { Button, CircularProgress, Typography } from '@material-ui/core'
import SaveIcon from '@material-ui/icons/Save'
import { useState } from 'react'
import { toast } from 'react-toastify'

import {
  ADMIN_CONFIG_LOG,
  MODIFY_ADMIN_CONFIG_LOG
} from '../../graphql/adminConfig'
import { getTranslate } from '../../localization'
import {
  AdminConfigLog,
  LogIntegrityStatus,
  ModifyAdminConfigLogInput
} from '../../types/adminConfig'
import FailedLogRetention from './components/FailedLogRetention'
import IntegrityStatus from './components/IntegrityStatus'
import LogStatus from './components/LogStatus'
import ThresholdSettings from './components/ThresholdSetting'
import useStyles from './styles/index.styles'

export default function AdminConfigLogTab() {
  const classes = useStyles()
  const [modifyAdminLogInput, setmodifyAdminLogInput] =
    useState<ModifyAdminConfigLogInput>({
      enabledLogging: false,
      thresholdSettings: {
        numberOfrecordes: 0,
        enabledSMSNotifications: false
      },
      failedLogRetention: {
        enabledSMSNotifications: false,
        retentionPeriodDays: 0
      }
    })
  const [integrityStatus, setIntegrityStatus] = useState<LogIntegrityStatus[]>(
    []
  )

  const {
    data: adminConfigLogData,
    loading: loadingForAdminConfig,
    refetch: refetchAdminConfig
  } = useQuery<{
    adminConfigLog: AdminConfigLog
  }>(ADMIN_CONFIG_LOG, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      setIntegrityStatus(res.adminConfigLog.integrityStatus)
      setmodifyAdminLogInput({
        enabledLogging: res.adminConfigLog.enabledLogging,
        thresholdSettings: {
          numberOfrecordes:
            res.adminConfigLog.thresholdSettings.numberOfRecords,
          enabledSMSNotifications:
            res.adminConfigLog.thresholdSettings.enabledSMSNotifications,
          intervalSMSNotifications:
            res.adminConfigLog.thresholdSettings.intervalSMSNotifications || 0,
          mobileNumbers:
            res.adminConfigLog.thresholdSettings.mobileNumbers || []
        },
        failedLogRetention: {
          enabledSMSNotifications:
            res.adminConfigLog.failedLogRetention.enabledSMSNotifications,
          retentionPeriodDays:
            res.adminConfigLog.failedLogRetention.retentionPeriodDays,
          intervalSMSNotifications:
            res.adminConfigLog.failedLogRetention.intervalSMSNotifications || 0,
          mobileNumbers:
            res.adminConfigLog.failedLogRetention.mobileNumbers || []
        }
      })
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const [modifyLog, { loading: loadingForModify }] = useMutation(
    MODIFY_ADMIN_CONFIG_LOG,
    {
      onCompleted: (res) => {
        toast.success(res.message)
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )

  return (
    <div className={classes.root}>
      <LogStatus
        modifyAdminLogInput={modifyAdminLogInput}
        setmodifyAdminLogInput={setmodifyAdminLogInput}
        loadingForAdminConfig={loadingForAdminConfig}
        totalRecords={
          adminConfigLogData?.adminConfigLog.totals.totalRecords || 0
        }
        integrityStatus={
          adminConfigLogData?.adminConfigLog.totals.integrityStatus || false
        }
      />
      <IntegrityStatus
        integrityStatus={integrityStatus}
        setIntegrityStatus={setIntegrityStatus}
        loadingForAdminConfig={loadingForAdminConfig}
      />
      <ThresholdSettings
        modifyAdminLogInput={modifyAdminLogInput}
        setmodifyAdminLogInput={setmodifyAdminLogInput}
        thresholdSettings={adminConfigLogData?.adminConfigLog.thresholdSettings}
      />
      <FailedLogRetention
        modifyAdminLogInput={modifyAdminLogInput}
        setmodifyAdminLogInput={setmodifyAdminLogInput}
      />
      <div className={classes.btnContainer}>
        <Button
          variant="outlined"
          onClick={() => {
            refetchAdminConfig()
          }}>
          <Typography variant="h4" className={classes.btnText}>
            {getTranslate('Reset')}
          </Typography>
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          className={classes.saveButton}
          onClick={() => {
            modifyLog({ variables: { input: modifyAdminLogInput } })
          }}>
          <>
            <Typography variant="h4" className={classes.whiteText}>
              {getTranslate('Save Log Settings')}
            </Typography>
            {loadingForModify && (
              <CircularProgress
                className={classes.circularProgress}
                size={16}
              />
            )}
          </>
        </Button>
      </div>
    </div>
  )
}
